/**
 * Importar o protocolo em vez de digitá-lo (E77.F1.T09, ADR-0078).
 *
 * O que esta tela promete, e por quê:
 *
 * - Os campos que voltam são **sugestão**, e dizem isso: cada um leva a marca da
 *   proveniência, e o aviso de que nada foi gravado fica visível até o perito
 *   salvar. O `PUT /protocolo` do formulário segue o único caminho de escrita.
 * - A leitura assistida é um botão **separado**, que só aparece quando sobrou
 *   campo, e o aviso de custo e de envio externo está no corpo — não numa dica
 *   que só aparece no hover. É a mesma escolha de "Atualizar fontes externas".
 * - Toda recusa vira uma frase que diz o que fazer a seguir, e o formulário
 *   manual continua onde estava. O atalho pode falhar; o caminho não pode.
 */
import { useRef, useState } from 'react';
import { Loader2, Upload } from 'lucide-react';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { ExameEletronicoError } from '@/lib/exame_eletronico';
import {
  importarProtocolo,
  lerResiduoDoProtocolo,
  type ProtocoloImportadoData,
} from '@/lib/exame_eletronico_importacao';
import { useApiClient } from '@/lib/api';

const t = ptBR.exameEletronico.importarProtocolo;
const tAus = ptBR.exameEletronico.resumoProtocolo.ausencias;

/** Campo do contrato → o rótulo que o perito reconhece na tela. */
const ROTULO_DO_CAMPO: Record<string, string> = {
  plataforma: tAus.plataforma,
  codigo_verificacao: tAus.codigoVerificacao,
  envelope_id: tAus.envelope,
  assinatura_id: tAus.assinaturaId,
  tipo_assinatura: tAus.tipoAssinatura,
  signatario: tAus.nome,
  eventos: tAus.eventos,
  ips: tAus.ips,
  coordenada: tAus.coordenada,
  dispositivo: tAus.dispositivo,
  biometria: ptBR.exameEletronico.resumoProtocolo.semBiometria,
};

function rotulos(campos: readonly string[]): string {
  return campos.map((campo) => ROTULO_DO_CAMPO[campo] ?? campo).join(', ');
}

function mensagemDoErro(chave: string): string {
  return (t.erros as Record<string, string>)[chave] ?? t.erros.documento_ilegivel;
}

/** O botão de envio, com o rótulo girando enquanto o documento é lido. */
function BotaoDeEnvio({
  enviando,
  desabilitado,
  onClick,
}: {
  enviando: boolean;
  desabilitado: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <Button
      type="button"
      variant="secondary"
      disabled={desabilitado}
      onClick={onClick}
      data-testid="importar-protocolo-enviar"
    >
      {enviando ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          {t.enviando}
        </>
      ) : (
        <>
          <Upload className="h-4 w-4" aria-hidden />
          {t.escolherArquivo}
        </>
      )}
    </Button>
  );
}

/** A ação paga, com o aviso de custo no corpo — nunca numa dica de hover. */
function AcaoAssistida({
  lendo,
  desabilitado,
  onClick,
}: {
  lendo: boolean;
  desabilitado: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <div className="space-y-sc-2 pt-sc-2">
      <p className="max-w-prose text-caption text-fg-dim">{t.leituraAssistidaAviso}</p>
      <Button
        type="button"
        variant="ghost"
        disabled={desabilitado}
        onClick={onClick}
        data-testid="importar-protocolo-residuo"
      >
        {lendo ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            {t.tentandoLeituraAssistida}
          </>
        ) : (
          t.tentarLeituraAssistida
        )}
      </Button>
    </div>
  );
}

interface ResultadoProps {
  importado: ProtocoloImportadoData;
  concluido: boolean;
  lendoResiduo: boolean;
  emVoo: boolean;
  onPedirResiduo: () => void;
}

/** O que a leitura produziu: quantos campos, o anexo, e o que continua faltando. */
function ResultadoDaLeitura({
  importado,
  concluido,
  lendoResiduo,
  emVoo,
  onPedirResiduo,
}: ResultadoProps): JSX.Element {
  const sugeridos = Object.keys(importado.sugestao.proveniencia);
  const restantes = Object.keys(ROTULO_DO_CAMPO).filter((campo) => !sugeridos.includes(campo));
  return (
    <div className="space-y-sc-2" data-testid="importar-protocolo-resultado">
      <p className="text-body-sm text-fg">
        {sugeridos.length === 1
          ? t.sugestaoTituloUm
          : t.sugestaoTitulo.replace('{n}', String(sugeridos.length))}
      </p>
      <p className="text-caption text-fg-dim" data-testid="importar-protocolo-aviso">
        {t.sugestaoAviso}
      </p>
      <p className="text-caption text-fg-dim">
        {t.arquivoAnexado.replace('{nome}', importado.anexo.nome_arquivo)}
      </p>
      <p className="break-all text-caption text-fg-dim">
        {t.codigoDeConferencia.replace('{valor}', importado.anexo.content_sha256)}
      </p>

      {importado.codigo_declarado_sugerido !== null && (
        <p className="break-all text-body-sm text-fg" data-testid="importar-protocolo-codigo">
          {t.codigoDaPecaSugerido.replace('{valor}', importado.codigo_declarado_sugerido)}
        </p>
      )}

      <p className="text-body-sm text-fg-muted" data-testid="importar-protocolo-resta">
        {restantes.length === 0
          ? t.restaVazio
          : t.restaTitulo.replace('{campos}', rotulos(restantes))}
      </p>

      {importado.residuo_disponivel && !concluido && (
        <AcaoAssistida lendo={lendoResiduo} desabilitado={emVoo} onClick={onPedirResiduo} />
      )}
    </div>
  );
}

export interface ImportarProtocoloCardProps {
  caseId: string;
  /** Bloqueado depois que o perito concluiu alguma peça (ADR-0077 §2.4). */
  concluido: boolean;
  /** Entrega a sugestão ao formulário, que a exibe pré-preenchida. */
  onSugestao: (importado: ProtocoloImportadoData) => void;
}

export function ImportarProtocoloCard({
  caseId,
  concluido,
  onSugestao,
}: ImportarProtocoloCardProps): JSX.Element {
  const client = useApiClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [lendoResiduo, setLendoResiduo] = useState(false);
  const [importado, setImportado] = useState<ProtocoloImportadoData | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const emVoo = enviando || lendoResiduo;

  async function enviar(arquivo: File): Promise<void> {
    setEnviando(true);
    setErro(null);
    try {
      const resposta = await importarProtocolo(client, caseId, arquivo);
      setImportado(resposta);
      onSugestao(resposta);
    } catch (err) {
      // Recusa não apaga o que já estava na tela: o perito pode ter uma
      // importação anterior boa e ter subido o arquivo errado agora.
      setErro(err instanceof ExameEletronicoError ? err.reason : 'documento_ilegivel');
    } finally {
      setEnviando(false);
      // Permite reenviar o MESMO arquivo depois de corrigi-lo fora do sistema.
      if (inputRef.current !== null) inputRef.current.value = '';
    }
  }

  async function pedirResiduo(): Promise<void> {
    setLendoResiduo(true);
    setErro(null);
    try {
      const resposta = await lerResiduoDoProtocolo(client, caseId);
      setImportado(resposta);
      onSugestao(resposta);
      // Falha do provedor volta 200 com motivo: informa sem fechar o caminho.
      if (resposta.leitura_falhou !== null) setErro(resposta.leitura_falhou);
    } catch (err) {
      setErro(err instanceof ExameEletronicoError ? err.reason : 'assistida_indisponivel');
    } finally {
      setLendoResiduo(false);
    }
  }

  return (
    <div className="space-y-sc-3 border-t border-border pt-sc-4" data-testid="importar-protocolo">
      <div>
        <h3 className="text-body-md text-fg">{t.titulo}</h3>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{t.intro}</p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="sr-only"
        aria-label={t.escolherArquivo}
        data-testid="importar-protocolo-input"
        disabled={emVoo || concluido}
        onChange={(evento) => {
          const arquivo = evento.target.files?.[0];
          if (arquivo !== undefined) void enviar(arquivo);
        }}
      />

      <BotaoDeEnvio
        enviando={enviando}
        desabilitado={emVoo || concluido}
        onClick={() => inputRef.current?.click()}
      />

      {erro !== null && (
        <p
          role="alert"
          className="text-fg-danger max-w-prose text-body-sm"
          data-testid="importar-protocolo-erro"
        >
          {mensagemDoErro(erro)}
        </p>
      )}

      {importado !== null && (
        <ResultadoDaLeitura
          importado={importado}
          concluido={concluido}
          lendoResiduo={lendoResiduo}
          emVoo={emVoo}
          onPedirResiduo={() => void pedirResiduo()}
        />
      )}
    </div>
  );
}
